import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useGetAvailableUniqueProductsWithSellers } from '~/api/product';
import { UniqueProductsCard } from '~/components/cards/UniqueProductsCard';
import type {
  AllUniqueProductsQuery,
  PlatformProduct,
  UniqueProductFiltersInput,
  UniqueProductWithSellers,
} from '~/graphql/graphql';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { ProductFilter, type ProductFilters } from './ProductFilter';
import { useCallback, useState } from 'react';

export function ProductsView() {
  const [filters, setFilters] = useState<UniqueProductFiltersInput>({});

  const { t } = useTranslation('product');

  const navigate = useNavigate();
  const returnPath = useLocation().pathname;

  const handleFiltering = useCallback(
    (uiFilters: ProductFilters) => {
      const updatedFilters: UniqueProductFiltersInput = {
        and: [],
      };

      if (uiFilters.category) {
        updatedFilters.and!.push({
          category: { eq: uiFilters.category },
        });
      }

      if (uiFilters.dimension) {
        const [thickness, width] = uiFilters.dimension.split('X');
        const tNum = parseFloat(thickness);
        const wNum = parseFloat(width);

        if (!isNaN(tNum) && !isNaN(wNum)) {
          updatedFilters.and!.push({
            nominalThicknessInches: { eq: tNum },
            nominalWidthInches: { eq: wNum },
          });
        }
      }

      if (uiFilters.location) {
        updatedFilters.and!.push({
          locationId: { eq: uiFilters.location },
        });
      }

      if (uiFilters.stockLevel) {
        switch (uiFilters.stockLevel) {
          case 'low':
            updatedFilters.and!.push({ stockLevel: { lt: 500 } });
            break;
          case 'medium':
            updatedFilters.and!.push({ stockLevel: { gte: 500, lte: 1500 } });
            break;
          case 'high':
            updatedFilters.and!.push({ stockLevel: { gt: 1500 } });
            break;
        }
      }

      if (uiFilters.searchTerm) {
        // A comma means "or" to the shared filter helper, and hundreds of product
        // names carry one ("LVL, 1-3/4 X 5-1/2"), so a pasted name would over-match.
        updatedFilters.and!.push({
          or: [{ name: { contains: uiFilters.searchTerm.replace(/,/g, ' ') } }],
        });
      }

      if (uiFilters.properties) {
        Object.entries(uiFilters.properties).forEach(([key, value]) => {
          if (value == null || value === '') return;

          if (!Array.isArray(value)) {
            updatedFilters.and!.push({
              properties: [{ key, operator: { eq: value } }],
            });
          } else if (value.length > 0) {
            updatedFilters.and!.push({
              or: value.map((v) => ({
                properties: [{ key, operator: { eq: v } }],
              })),
            });
          }
        });
      }

      if (updatedFilters.and?.length === 0) {
        setFilters({});
      } else {
        setFilters(updatedFilters);
      }
    },
    [setFilters]
  );

  const {
    data: products,
    isLoading,
    hasMoreData,
    sentinelRef,
  } = usePaginatedQuery<
    AllUniqueProductsQuery,
    UniqueProductWithSellers,
    UniqueProductFiltersInput
  >({
    fetchHook: useGetAvailableUniqueProductsWithSellers,
    select: (data) => data?.findAvailableUniqueProductsWithSellers,
    options: { filters },
  });

  return (
    <div className="space-y-4">
      <ProductFilter onChange={handleFiltering} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
        {products.map((product) => (
          <UniqueProductsCard
            key={product.id}
            onClick={() =>
              navigate(
                `/order-builder/view?platformProductId=${product!.platformProduct.id}&returnPath=${returnPath}`
              )
            }
            platformProduct={product.platformProduct as PlatformProduct}
            marketAvailability={product.marketAvailability ?? null}
            landedPriceRange={product.landedPriceRange ?? null}
            lengths={
              (product.lengths ?? []).filter((length) => length != null) || []
            }
          />
        ))}
      </div>
      <div
        style={{ height: '1px', background: 'transparent' }}
        ref={sentinelRef}
      ></div>
      {isLoading ? <div>{t('product.loading')}</div> : null}
      {hasMoreData === false && !isLoading && products.length > 0 ? (
        <p className="flex w-full justify-center">{t('product.noMoreData')}</p>
      ) : null}
      {(!products || products?.length === 0) && !isLoading ? (
        <p data-testid="no-products" className="flex w-full justify-center">
          {t('product.no_products')}
        </p>
      ) : null}
    </div>
  );
}
